import type {
  NationCapacity,
  ResourceStockpile,
} from '../core/economyTypes'
import {
  PROTOTYPE_REGIONS,
  type RegionDefinition,
  type ResourceFocus,
} from './regions'
import {
  getNationDefinition,
  type MajorPowerId,
  type NationId,
} from './nations'

export type WorldRegionStatus = 'active' | 'planned'
export type WorldRegionRole =
  | 'major-core'
  | 'client-state'
  | 'minor-state'
export type WorldSphereId = MajorPowerId | 'unaligned'
export type StrategicResourceProfile =
  | 'industrial'
  | 'fuel'
  | 'electronics'
  | 'research'
  | 'balanced'

export interface WorldMapRegionDefinition {
  id: string
  name: string
  ownerId: NationId
  ownerName: string
  patronId: MajorPowerId | null
  sphereId: WorldSphereId
  role: WorldRegionRole
  status: WorldRegionStatus
  runtimeRegionId: string | null
  terrain: string
  resourceFocus: ResourceFocus
  resourceProfile: StrategicResourceProfile
  resourceLabel: string
  slots: number
  production: ResourceStockpile
  capacity: NationCapacity
  x: number
  y: number
  width: number
  height: number
  neighbors: string[]
}

interface Placement {
  x: number
  y: number
  width: number
  height: number
  neighbors: string[]
}

const ACTIVE_PLACEMENTS: Record<string, Placement> = {
  'star-west': {
    x: 40, y: 240, width: 140, height: 78,
    neighbors: ['star-central', 'southcape'],
  },
  'star-central': {
    x: 195, y: 180, width: 140, height: 78,
    neighbors: ['star-west', 'star-south', 'star-east'],
  },
  'star-south': {
    x: 170, y: 335, width: 140, height: 78,
    neighbors: ['star-central', 'star-east', 'southcape'],
  },
  'star-east': {
    x: 340, y: 265, width: 140, height: 78,
    neighbors: ['star-central', 'star-south', 'isles'],
  },
  isles: {
    x: 390, y: 120, width: 108, height: 62,
    neighbors: ['star-east', 'britannia-home'],
  },
  southcape: {
    x: 300, y: 475, width: 112, height: 62,
    neighbors: ['star-west', 'star-south', 'francia-client'],
  },
  northsea: {
    x: 1015, y: 80, width: 108, height: 62,
    neighbors: ['britannia-northsea', 'red-west', 'red-north'],
  },
  steppe: {
    x: 1090, y: 470, width: 112, height: 62,
    neighbors: ['highland-zone', 'red-central', 'red-east'],
  },
  'red-west': {
    x: 985, y: 180, width: 140, height: 78,
    neighbors: ['northsea', 'red-central', 'red-north'],
  },
  'red-central': {
    x: 1120, y: 285, width: 140, height: 78,
    neighbors: ['red-west', 'red-north', 'red-east', 'steppe'],
  },
  'red-north': {
    x: 1165, y: 95, width: 140, height: 78,
    neighbors: ['northsea', 'red-west', 'red-central', 'zhonghua-inland'],
  },
  'red-east': {
    x: 1250, y: 405, width: 140, height: 78,
    neighbors: ['red-central', 'red-north', 'steppe', 'zhonghua-coast'],
  },
}

function profileFromFocus(
  focus: ResourceFocus,
): StrategicResourceProfile {
  if (focus === 'metal') return 'industrial'
  if (focus === 'fuel') return 'fuel'
  if (focus === 'electronics') return 'electronics'
  return 'balanced'
}

const ACTIVE_WORLD_REGIONS: WorldMapRegionDefinition[] =
  PROTOTYPE_REGIONS.map((region: RegionDefinition) => {
    const placement = ACTIVE_PLACEMENTS[region.id]
    if (!placement) {
      throw new Error(`Missing world-map placement for active region: ${region.id}`)
    }

    return {
      id: region.id,
      name: region.name,
      ownerId: region.ownerId,
      ownerName: region.ownerName,
      patronId: region.patronId,
      sphereId: region.patronId,
      role: region.role,
      status: 'active',
      runtimeRegionId: region.id,
      terrain: region.terrain,
      resourceFocus: region.resourceFocus,
      resourceProfile: profileFromFocus(region.resourceFocus),
      resourceLabel: region.resourceLabel,
      slots: region.slots,
      production: { ...region.production },
      capacity: { ...region.capacity },
      ...placement,
    }
  })

const PLANNED_WORLD_REGIONS: WorldMapRegionDefinition[] = [
  {
    id: 'britannia-home',
    name: '本土工业群岛',
    ownerId: 'britannia',
    ownerName: getNationDefinition('britannia').displayName,
    patronId: null,
    sphereId: 'britannia',
    role: 'major-core',
    status: 'planned',
    runtimeRegionId: null,
    terrain: '群岛与沿海城市群',
    resourceFocus: 'electronics',
    resourceProfile: 'electronics',
    resourceLabel: '航空电子与精密制造',
    slots: 4,
    production: { money: 8, metal: 2, fuel: 1, electronics: 5 },
    capacity: { power: 2, research: 3 },
    x: 525, y: 120, width: 122, height: 72,
    neighbors: ['britannia-northsea', 'britannia-client', 'isles', 'francia-west'],
  },
  {
    id: 'britannia-northsea',
    name: '北海试验区',
    ownerId: 'britannia',
    ownerName: getNationDefinition('britannia').displayName,
    patronId: null,
    sphereId: 'britannia',
    role: 'major-core',
    status: 'planned',
    runtimeRegionId: null,
    terrain: '寒冷海岸与试验场',
    resourceFocus: 'fuel',
    resourceProfile: 'research',
    resourceLabel: '试验设施与燃料',
    slots: 4,
    production: { money: 6, metal: 2, fuel: 4, electronics: 2 },
    capacity: { power: 2, research: 2 },
    x: 550, y: 220, width: 122, height: 72,
    neighbors: ['britannia-home', 'northsea', 'francia-west'],
  },
  {
    id: 'britannia-client',
    name: '远洋共同体',
    ownerId: 'oceanic-commonwealth',
    ownerName: getNationDefinition('oceanic-commonwealth').displayName,
    patronId: 'britannia',
    sphereId: 'britannia',
    role: 'client-state',
    status: 'planned',
    runtimeRegionId: null,
    terrain: '远洋群岛与基地',
    resourceFocus: 'fuel',
    resourceProfile: 'fuel',
    resourceLabel: '燃料与远洋基地',
    slots: 2,
    production: { money: 4, metal: 1, fuel: 4, electronics: 1 },
    capacity: { power: 1, research: 0 },
    x: 500, y: 35, width: 108, height: 58,
    neighbors: ['britannia-home'],
  },
  {
    id: 'francia-west',
    name: '西陆工业带',
    ownerId: 'francia',
    ownerName: getNationDefinition('francia').displayName,
    patronId: null,
    sphereId: 'francia',
    role: 'major-core',
    status: 'planned',
    runtimeRegionId: null,
    terrain: '沿海平原与城市带',
    resourceFocus: 'electronics',
    resourceProfile: 'balanced',
    resourceLabel: '综合工业与电子',
    slots: 4,
    production: { money: 8, metal: 3, fuel: 2, electronics: 4 },
    capacity: { power: 2, research: 2 },
    x: 720, y: 165, width: 128, height: 74,
    neighbors: ['francia-south', 'francia-client', 'britannia-home', 'britannia-northsea', 'strait-zone'],
  },
  {
    id: 'francia-south',
    name: '南部推进试验区',
    ownerId: 'francia',
    ownerName: getNationDefinition('francia').displayName,
    patronId: null,
    sphereId: 'francia',
    role: 'major-core',
    status: 'planned',
    runtimeRegionId: null,
    terrain: '丘陵与试验场',
    resourceFocus: 'fuel',
    resourceProfile: 'research',
    resourceLabel: '推进试验与燃料',
    slots: 4,
    production: { money: 6, metal: 2, fuel: 5, electronics: 2 },
    capacity: { power: 2, research: 2 },
    x: 720, y: 295, width: 128, height: 74,
    neighbors: ['francia-west', 'francia-client', 'highland-zone'],
  },
  {
    id: 'francia-client',
    name: '赤道共同体',
    ownerId: 'equator-commonwealth',
    ownerName: getNationDefinition('equator-commonwealth').displayName,
    patronId: 'francia',
    sphereId: 'francia',
    role: 'client-state',
    status: 'planned',
    runtimeRegionId: null,
    terrain: '赤道低地与沿海',
    resourceFocus: 'fuel',
    resourceProfile: 'fuel',
    resourceLabel: '燃料与发射支援',
    slots: 2,
    production: { money: 4, metal: 2, fuel: 4, electronics: 0 },
    capacity: { power: 1, research: 0 },
    x: 650, y: 465, width: 112, height: 62,
    neighbors: ['francia-south', 'southcape', 'highland-zone'],
  },
  {
    id: 'highland-zone',
    name: '高地共和国',
    ownerId: 'highland-republic',
    ownerName: getNationDefinition('highland-republic').displayName,
    patronId: null,
    sphereId: 'unaligned',
    role: 'minor-state',
    status: 'planned',
    runtimeRegionId: null,
    terrain: '高原与矿区',
    resourceFocus: 'metal',
    resourceProfile: 'industrial',
    resourceLabel: '矿产与通用工业',
    slots: 2,
    production: { money: 4, metal: 5, fuel: 1, electronics: 0 },
    capacity: { power: 1, research: 0 },
    x: 850, y: 470, width: 112, height: 62,
    neighbors: ['francia-south', 'francia-client', 'steppe', 'strait-zone'],
  },
  {
    id: 'strait-zone',
    name: '海峡自由国',
    ownerId: 'strait-free-state',
    ownerName: getNationDefinition('strait-free-state').displayName,
    patronId: null,
    sphereId: 'unaligned',
    role: 'minor-state',
    status: 'planned',
    runtimeRegionId: null,
    terrain: '海峡与港口群',
    resourceFocus: 'electronics',
    resourceProfile: 'electronics',
    resourceLabel: '贸易与电子工业',
    slots: 2,
    production: { money: 6, metal: 1, fuel: 1, electronics: 3 },
    capacity: { power: 1, research: 1 },
    x: 860, y: 285, width: 112, height: 62,
    neighbors: ['francia-west', 'highland-zone', 'red-west'],
  },
  {
    id: 'zhonghua-coast',
    name: '东洲工业走廊',
    ownerId: 'zhonghua',
    ownerName: getNationDefinition('zhonghua').displayName,
    patronId: null,
    sphereId: 'zhonghua',
    role: 'major-core',
    status: 'planned',
    runtimeRegionId: null,
    terrain: '沿海工业带',
    resourceFocus: 'electronics',
    resourceProfile: 'balanced',
    resourceLabel: '制造业与电子工业',
    slots: 4,
    production: { money: 8, metal: 4, fuel: 2, electronics: 4 },
    capacity: { power: 3, research: 2 },
    x: 1460, y: 225, width: 148, height: 80,
    neighbors: ['zhonghua-inland', 'zhonghua-client', 'red-east'],
  },
  {
    id: 'zhonghua-inland',
    name: '内陆科研基地',
    ownerId: 'zhonghua',
    ownerName: getNationDefinition('zhonghua').displayName,
    patronId: null,
    sphereId: 'zhonghua',
    role: 'major-core',
    status: 'planned',
    runtimeRegionId: null,
    terrain: '高原与内陆工业区',
    resourceFocus: 'metal',
    resourceProfile: 'research',
    resourceLabel: '科研与重工业',
    slots: 4,
    production: { money: 7, metal: 5, fuel: 2, electronics: 2 },
    capacity: { power: 3, research: 3 },
    x: 1410, y: 350, width: 148, height: 80,
    neighbors: ['zhonghua-coast', 'zhonghua-client', 'red-north'],
  },
  {
    id: 'zhonghua-client',
    name: '玉海共同体',
    ownerId: 'jade-coast-commonwealth',
    ownerName: getNationDefinition('jade-coast-commonwealth').displayName,
    patronId: 'zhonghua',
    sphereId: 'zhonghua',
    role: 'client-state',
    status: 'planned',
    runtimeRegionId: null,
    terrain: '群岛与沿海低地',
    resourceFocus: 'electronics',
    resourceProfile: 'electronics',
    resourceLabel: '电子元件与港口制造',
    slots: 2,
    production: { money: 4, metal: 1, fuel: 1, electronics: 4 },
    capacity: { power: 1, research: 1 },
    x: 1535, y: 475, width: 112, height: 62,
    neighbors: ['zhonghua-coast', 'zhonghua-inland'],
  },
]

export const WORLD_MAP_REGIONS: WorldMapRegionDefinition[] = [
  ...ACTIVE_WORLD_REGIONS,
  ...PLANNED_WORLD_REGIONS,
]

export function getWorldMapRegion(
  regionId: string,
): WorldMapRegionDefinition | undefined {
  return WORLD_MAP_REGIONS.find((region) => region.id === regionId)
}

export function getWorldMapSphereLabel(
  sphereId: WorldSphereId,
): string {
  if (sphereId === 'unaligned') {
    return '未结盟 / 独立'
  }

  return getNationDefinition(sphereId).displayName
}
